import { useCallback, useRef, useState } from 'react';

export interface PageInfo {
  hasNextPage: boolean;
  endCursor?: string;
}

export type FetchMoreFn<TData> = (cursor: string) => Promise<{ data: TData }>;

export type PageInfoResolver<TData> = (data: TData) => PageInfo | undefined;

export interface UseEagerLoadAllRecordsProps<TData> {
  data: TData | undefined;
  fetchMore: FetchMoreFn<TData>;
  getPageInfo: PageInfoResolver<TData>;
  onError?: (error: Error) => void;
}

export function useEagerLoadAllRecords<TData>(props: UseEagerLoadAllRecordsProps<TData>) {
  const { data, fetchMore, getPageInfo, onError } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dataRef = useRef<TData | undefined>(data);
  dataRef.current = data;

  const eagerLoadAllRecords = useCallback(async () => {
    setLoading(true);

    const currentData = dataRef.current;

    if (!currentData) {
      return;
    }

    try {
      let pageInfo = getPageInfo(currentData);

      while (pageInfo?.hasNextPage && pageInfo.endCursor) {
        const result = await fetchMore(pageInfo.endCursor);

        pageInfo = result?.data ? getPageInfo(result.data) : undefined;
      }
    } catch (e) {
      setError(e);

      if (onError) {
        onError(e);
      } else {
        throw e;
      }
    } finally {
      setLoading(false);
    }
  }, [getPageInfo, fetchMore, onError, setLoading]);

  return [
    eagerLoadAllRecords,
    {
      loading,
      error,
    },
  ] as const;
}
